import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { endpoints } from "@/config/endpoints";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, X, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/auth.service";
import AddProductModal from "@/components/AddProductModal";
import { Checkbox } from "@/components/ui/checkbox";
import AddSupplierModal from "@/components/AddSupplierModal";
interface Supplier {
  id: string;
  _id?: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  category: string;
  products: {
    name: string;
    description: string;
    quality: string;
    _id: string;
  }[];
  status: "active" | "inactive";
  userId: {
    _id: string;
    name: string;
    email: string;
    roles: string[];
    status: string;
  };
}

const Suppliers = () => {
  const queryClient = useQueryClient();
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );
  const [updateForm, setUpdateForm] = useState<Partial<Supplier>>({});
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [selectedSupplierForProduct, setSelectedSupplierForProduct] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const itemsPerPage = 10;

  const {
    data: suppliers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const { token, user } = authService.getStoredAuth();
      console.log("Current user:", user);
      console.log("Token exists:", !!token);

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(endpoints.suppliers.list, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log("Error response:", errorData);

        if (response.status === 401) {
          try {
            console.log("Attempting token refresh...");
            const newToken = await authService.refreshToken();
            console.log("Token refresh successful");

            const retryResponse = await fetch(endpoints.suppliers.list, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });

            console.log("Retry response status:", retryResponse.status);
            if (!retryResponse.ok) {
              const retryError = await retryResponse.json().catch(() => ({}));
              console.log("Retry error:", retryError);
              throw new Error("Failed to fetch suppliers after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            console.error("Token refresh failed:", error);
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error(errorData.error || "Failed to fetch suppliers");
      }
      return response.json();
    },
  });

  const updateSupplierMutation = useMutation({
    mutationFn: async (updatedSupplier: Supplier) => {
      const supplierId = updatedSupplier._id || updatedSupplier.id;
      const { token } = authService.getStoredAuth();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(endpoints.suppliers.update(supplierId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedSupplier),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update supplier");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Supplier updated successfully");
      setSelectedSupplier(null);
      setUpdateForm({});
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update supplier");
    },
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (supplierIds: string | string[]) => {
      const { token } = authService.getStoredAuth();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const isBulkDelete = Array.isArray(supplierIds);
      const endpoint = isBulkDelete
        ? endpoints.suppliers.bulkDelete
        : endpoints.suppliers.delete(supplierIds as string);

      const response = await fetch(endpoint, {
        method: isBulkDelete ? "POST" : "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        ...(isBulkDelete && { body: JSON.stringify({ supplierIds }) }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(endpoint, {
              method: isBulkDelete ? "POST" : "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              ...(isBulkDelete && { body: JSON.stringify({ supplierIds }) }),
            });
            if (!retryResponse.ok) {
              throw new Error(
                "Failed to delete supplier(s) after token refresh"
              );
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to delete supplier(s)");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      setSelectedSuppliers([]);
      toast.success("Supplier(s) deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete supplier(s)");
    },
  });

  const handleDelete = (supplierId: string) => {
    deleteSupplierMutation.mutate(supplierId);
  };

  const handleBulkDelete = () => {
    if (selectedSuppliers.length === 0) {
      toast.error("Please select suppliers to delete");
      return;
    }
    deleteSupplierMutation.mutate(selectedSuppliers);
  };

  const handleSelectSupplier = (supplierId: string) => {
    setSelectedSuppliers((prev) =>
      prev.includes(supplierId)
        ? prev.filter((id) => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSuppliers.length === currentSuppliers?.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(
        currentSuppliers?.map((supplier) => supplier._id || supplier.id) || []
      );
    }
  };

  const handleUpdate = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setUpdateForm(supplier);
  };

  const handleUpdateSubmit = () => {
    if (selectedSupplier) {
      updateSupplierMutation.mutate({ ...selectedSupplier, ...updateForm });
    }
  };

  const handleAddProduct = (supplierId: string) => {
    setSelectedSupplierForProduct(supplierId);
    setIsAddProductModalOpen(true);
  };

  const handleProductAdded = () => {
    queryClient.invalidateQueries({ queryKey: ["suppliers"] });
  };

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers?.filter((supplier: Supplier) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      supplier.name.toLowerCase().includes(searchLower) ||
      supplier.email.toLowerCase().includes(searchLower) ||
      supplier.phone.toLowerCase().includes(searchLower) ||
      supplier.location.toLowerCase().includes(searchLower) ||
      supplier.category.toLowerCase().includes(searchLower) ||
      supplier.products.some(
        (product) =>
          product.name.toLowerCase().includes(searchLower) ||
          product.description.toLowerCase().includes(searchLower)
      )
    );
  });

  // Calculate pagination with filtered suppliers
  const totalPages = Math.ceil((filteredSuppliers?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSuppliers = filteredSuppliers?.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <DashboardLayout title="Suppliers">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    toast.error("Failed to load suppliers");
    return (
      <DashboardLayout title="Suppliers">
        <div className="text-center text-red-500">Error loading suppliers</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Suppliers</CardTitle>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Input
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[300px]"
              />
            </div>
            {selectedSuppliers.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
              >
                <Trash2 size={16} className="mr-2" />
                Delete Selected ({selectedSuppliers.length})
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              className="bg-orange-500 hover:bg-orange-600"
              onClick={() => setIsAddUserModalOpen(true)}
            >
              <Plus size={16} className="mr-2" />
              Add Supplier
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={
                      selectedSuppliers.length === currentSuppliers?.length
                    }
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentSuppliers?.map((supplier: Supplier) => (
                <TableRow key={supplier._id || supplier.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedSuppliers.includes(
                        supplier._id || supplier.id
                      )}
                      onCheckedChange={() =>
                        handleSelectSupplier(supplier._id || supplier.id)
                      }
                    />
                  </TableCell>
                  <TableCell>{supplier.name}</TableCell>
                  <TableCell>{supplier.email}</TableCell>
                  <TableCell>{supplier.phone}</TableCell>
                  <TableCell>{supplier.location}</TableCell>
                  <TableCell>{supplier.category}</TableCell>
                  <TableCell>
                    {supplier.userId?.roles?.map((role) => (
                      <Badge key={role} variant="outline" className="mr-1">
                        {role}
                      </Badge>
                    ))}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {supplier.products?.map((product) => (
                        <div key={product._id} className="text-sm">
                          <span className="font-medium">{product.name}</span>
                          {/* {product.description && (
                            <span className="text-muted-foreground">
                              {" "}
                              - {product.description}
                            </span>
                          )} */}
                          {product.quality && (
                            <span className="text-muted-foreground">
                              {" "}
                              ({product.quality})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        supplier.status === "active" ? "default" : "destructive"
                      }
                    >
                      {supplier.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleUpdate(supplier)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Are you sure you want to delete this supplier?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will
                              permanently delete the supplier and all associated
                              data.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                handleDelete(supplier._id || supplier.id)
                              }
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, suppliers?.length || 0)} of{" "}
              {suppliers?.length || 0} suppliers
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedSupplier}
        onOpenChange={() => setSelectedSupplier(null)}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update Supplier</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={updateForm.name || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={updateForm.email || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, email: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={updateForm.phone || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, phone: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={updateForm.location || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, location: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={updateForm.category || ""}
                onChange={(e) =>
                  setUpdateForm({
                    ...updateForm,
                    category: e.target.value,
                  })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select category</option>
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={updateForm.status || ""}
                onChange={(e) =>
                  setUpdateForm({
                    ...updateForm,
                    status: e.target.value as "active" | "inactive",
                  })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSupplier(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSubmit}
              disabled={updateSupplierMutation.isPending}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddProductModal
        isOpen={isAddProductModalOpen}
        onClose={() => {
          setIsAddProductModalOpen(false);
          setSelectedSupplierForProduct(null);
        }}
        supplierId={selectedSupplierForProduct || ""}
        onProductAdded={handleProductAdded}
      />

      <AddSupplierModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["suppliers"] });
        }}
      />
    </DashboardLayout>
  );
};

export default Suppliers;
